import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { ShieldCheck, Loader2, Lock, KeyRound, Fingerprint } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { navy: '#2B4C7E', gold: '#C8A97E', cream: '#FDFBF7', ink: '#1C252E', mute: '#8D8880', line: '#E3E1DC' };
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
const pinInput = 'w-full h-12 px-4 rounded-xl border border-[#E3E1DC] text-center text-lg tracking-[0.4em] outline-none focus:border-[#2B4C7E]';

// Session-level Security PIN gate. Verify once per login; unlocks the wrapped screen
// until logout/refresh-token-reset. Used to protect sensitive areas (credentials, savings, records).
export default function PinGate({ children, title = 'Protected area' }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading | setup | verify | unlocked
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [bioRegistered, setBioRegistered] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/security/status`, authH());
        setBioRegistered(!!r.data.biometric_registered);
        if (r.data.pin_verified) setPhase('unlocked');
        else setPhase(r.data.has_pin ? 'verify' : 'setup');
      } catch {
        setPhase('verify');
      }
    })();
  }, []);

  const bioUnlock = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const opts = (await axios.post(`${API}/security/webauthn/auth/begin`, { origin }, authH())).data;
      const assertion = await startAuthentication({ optionsJSON: opts });
      const r = await axios.post(`${API}/security/webauthn/auth/complete`, { credential: assertion, origin }, authH());
      if (r.data.token) localStorage.setItem('session_token', r.data.token);
      setPhase('unlocked');
      toast.success('Unlocked with biometric');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Biometric unlock failed — use your PIN');
    } finally { setBusy(false); }
  };

  const bioEnroll = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const opts = (await axios.post(`${API}/security/webauthn/register/begin`, { origin }, authH())).data;
      const att = await startRegistration({ optionsJSON: opts });
      await axios.post(`${API}/security/webauthn/register/complete`, { credential: att, origin }, authH());
      setBioRegistered(true);
      toast.success('Biometric enabled — next time, unlock with Face ID / fingerprint');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not enable biometric on this device');
    } finally { setBusy(false); }
  };

  const verify = async (thePin) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/security/verify-pin`, { pin: thePin }, authH());
      if (r.data.token) localStorage.setItem('session_token', r.data.token);
      setPhase('unlocked');
      toast.success('Unlocked');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Incorrect PIN');
      setPin('');
    } finally { setBusy(false); }
  };

  const setupAndVerify = async () => {
    if (!/^\d{4,6}$/.test(pin)) { toast.error('PIN must be 4–6 digits'); return; }
    if (pin !== confirmPin) { toast.error('PINs do not match'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/security/setup-pin`, { pin }, authH());
      await verify(pin);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set up PIN');
      setBusy(false);
    }
  };

  if (phase === 'unlocked') return children;

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.cream }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.cream }} data-testid="pin-gate">
      <div className="bg-white rounded-3xl border border-[#EDE7DC] p-8 w-full max-w-md shadow-lg">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: C.navy }}>
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>

        {phase === 'setup' ? (
          <>
            <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Set up your Security PIN</h1>
            <p className="text-sm mt-1 mb-5" style={{ color: C.mute }}>
              {title} is sensitive. Create a 4–6 digit PIN. You’ll enter it once each time you log in to unlock protected areas.
            </p>
            <label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>New PIN</label>
            <input type="password" inputMode="numeric" maxLength={6} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className={pinInput} placeholder="••••" data-testid="pin-setup-input" />
            <label className="block text-xs font-semibold mb-1 mt-3" style={{ color: '#57534E' }}>Confirm PIN</label>
            <input type="password" inputMode="numeric" maxLength={6} value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && setupAndVerify()}
              className={pinInput} placeholder="••••" data-testid="pin-confirm-input" />
            <button onClick={setupAndVerify} disabled={busy}
              className="mt-5 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: C.navy }} data-testid="pin-setup-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Create PIN &amp; unlock
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold" style={{ color: C.ink }}>Enter your Security PIN</h1>
            <p className="text-sm mt-1 mb-5" style={{ color: C.mute }}>
              For your security, verify your PIN to access {title.toLowerCase()}.
            </p>

            {bioRegistered && (
              <>
                <button onClick={bioUnlock} disabled={busy}
                  className="w-full h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 mb-4"
                  style={{ background: C.gold }} data-testid="pin-biometric-unlock">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-5 w-5" />} Unlock with Face ID / fingerprint
                </button>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1" style={{ background: C.line }} />
                  <span className="text-xs" style={{ color: C.mute }}>or use your PIN</span>
                  <div className="h-px flex-1" style={{ background: C.line }} />
                </div>
              </>
            )}

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: C.mute }} />
              <input type="password" inputMode="numeric" maxLength={6} value={pin} autoFocus
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && pin.length >= 4 && verify(pin)}
                className={`${pinInput} pl-10`} placeholder="••••" data-testid="pin-verify-input" />
            </div>
            <button onClick={() => verify(pin)} disabled={busy || pin.length < 4}
              className="mt-5 w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: C.navy }} data-testid="pin-verify-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Unlock
            </button>

            {!bioRegistered && (
              <button onClick={bioEnroll} disabled={busy}
                className="mt-3 w-full text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                style={{ color: C.navy }} data-testid="pin-biometric-enroll">
                <Fingerprint className="h-3.5 w-3.5" /> Enable Face ID / fingerprint unlock on this device
              </button>
            )}
          </>
        )}

        <button onClick={() => navigate('/dashboard')}
          className="mt-3 w-full text-xs font-medium" style={{ color: C.mute }} data-testid="pin-gate-cancel">
          Cancel &amp; return to dashboard
        </button>
      </div>
    </div>
  );
}
